import React from 'react';
import { motion } from 'framer-motion';

const VloaderPage = () => {
    const features = [
        { title: 'Multi-Source Download', text: 'Save videos from various platforms easily.', icon: '📥' },
        { title: 'High Quality', text: 'Preserve original video quality up to 4K.', icon: '🎬' },
        { title: 'Batch Downloads', text: 'Process multiple downloads simultaneously.', icon: '⚡' },
        { title: 'Cloud Integration', text: 'Sync with cloud services seamlessly.', icon: '☁️' },
    ];

    return (
        <div className="min-h-screen bg-dark pt-24 pb-16">
            {/* Background orbs */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-20 right-10 w-72 h-72 bg-accent-cyan opacity-15 rounded-full blur-3xl" />
                <div className="absolute top-60 left-20 w-96 h-96 bg-accent-blue opacity-10 rounded-full blur-3xl" />
                <div className="absolute bottom-20 right-1/3 w-80 h-80 bg-accent-purple opacity-10 rounded-full blur-3xl" />
            </div>

            <div className="max-w-6xl mx-auto px-6 relative z-10">
                {/* Hero Section */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="text-center mb-16"
                >
                    <h1 className="text-4xl md:text-5xl font-custom1 text-white mb-2">
                        Vloader
                    </h1>
                    <span className="bg-gradient-to-r from-accent-cyan to-accent-blue bg-clip-text text-transparent font-custom2 text-xl block mb-6">
                        Video Downloading App
                    </span>

                    <p className="text-lg font-custom2 text-gray-400 mb-8 leading-relaxed max-w-3xl mx-auto">
                        Vloader is a cutting-edge video downloading application that allows you to save and manage high-quality videos from various sources. Whether you're a content creator, a casual viewer, or someone looking to organize important video assets, Vloader offers an intuitive platform where convenience meets efficiency.
                    </p>

                    {/* Store Badges */}
                    <div className="flex gap-4 justify-center">
                    </div>
                </motion.div>

                {/* Features */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                    className="mb-16"
                >
                    <h2 className="text-2xl font-custom1 text-white mb-8 text-center">Features</h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {features.map((feature, idx) => (
                            <motion.div
                                key={feature.title}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.4 + idx * 0.1 }}
                                className="bg-dark-card border border-gray-600 rounded-2xl p-6 hover:border-accent-cyan transition-colors duration-300 text-center"
                            >
                                <span className="text-3xl mb-4 block">{feature.icon}</span>
                                <h3 className="text-lg font-custom1 text-white mb-2">{feature.title}</h3>
                                <p className="text-sm font-custom2 text-gray-400">{feature.text}</p>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* About */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.5 }}
                    className="bg-dark-card border border-gray-600 rounded-2xl p-8 mb-16"
                >
                    <h2 className="text-2xl font-custom1 text-white mb-6">About Vloader</h2>
                    <div className="space-y-4 text-gray-400 font-custom2 leading-relaxed">
                        <p>
                            With its user-friendly interface, Vloader makes it easy for anyone to download and store videos without the hassle of complicated software or technical hurdles. The intelligent engine is designed to recognize and handle different video formats, ensuring that you can save everything from short clips to full-length features.
                        </p>
                        <p>
                            One of the most exciting features of Vloader is its ability to process multiple downloads simultaneously, all while preserving high-quality resolution. You can customize the video quality, select preferred file formats, and organize downloads into user-friendly folders.
                        </p>
                        <p>
                            Additionally, Vloader is continually updated with new features and improvements, ensuring that you always have access to the latest tools in video downloading.
                        </p>
                    </div>
                </motion.div>

                {/* Policies */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.6 }}
                    className="text-center mb-10"
                >
                    <p className="text-gray-500 font-custom2 text-sm">
                        By using Vloader, you agree to our{' '}
                        <a href="/vloader/privacy-policy" className="text-accent-cyan hover:underline">Privacy Policy</a>,{' '}
                        <a href="/vloader/terms-and-conditions" className="text-accent-cyan hover:underline">Terms and Conditions</a>, and{' '}
                        <a href="/vloader/eula" className="text-accent-cyan hover:underline">EULA</a>.
                    </p>
                </motion.div>

                {/* Footer */}
                <div className="text-center">
                    <p className="text-gray-600 font-custom2 text-sm">© 2024 Vloader. All rights reserved.</p>
                </div>
            </div>
        </div>
    );
};

export default VloaderPage;
